import {useTranslation} from 'react-i18next';

import {useModal} from '@hooks';

import {Icon} from '@atoms';
import {OfficeWorkday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {Avatar, Button} from '@molecules';
import {ComponentProps} from 'react';
import {TileItem} from '../../TileItem';
import {TileMapPreview_timeslots} from '../../TileMapPreview_timeslots';
import {BookingDetailsCard_timeslots} from 'src/components/organisms/bookWorkspace/BookingDetailsCard/timeslots/BookingDetailsCard_timeslots';
import {trackEvent} from '@lib/infrastructure';
import {useReservationDetails} from '@lib/store/src/ui/hooks/timeslots/useReservationDetails';
import {getGroupInvitationExpiryTime} from '@lib/utils';
import {useAcceptDeclineWorkspaceInvitation_timeslots} from 'src/hooks/useAcceptDeclineWorkdayInvitation_timeslots';

export const GroupBookingTile_timeslots = ({
  date,
  workspaceReservation,
  workday,
  showMapPreview,
  ...props
}: ComponentProps<typeof TileItem> & {
  date: string;
  workspaceReservation: WorkspaceReservation;
  workday: OfficeWorkday | null;
  showMapPreview?: boolean;
}) => {
  const {t} = useTranslation();
  const {openModal, setModalPages} = useModal();

  const details = useReservationDetails(workspaceReservation);

  const owner = details.status === 'fulfilled' ? details.data.find((d) => d.isOrganizer)?.owner : undefined;
  const currentMember = details.status === 'fulfilled' ? details.data.find((d) => d.isCurrentUser) : undefined;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.click();
    }
  };

  const {isSaving, onAcceptInvitation, onDeclineInvitation} =
    useAcceptDeclineWorkspaceInvitation_timeslots(workspaceReservation);

  const acceptInvitation = async () => {
    onAcceptInvitation();
  };

  const declineInvitation = async () => {
    onDeclineInvitation();
  };

  if (!owner) return null;

  return (
    <>
      {showMapPreview && workspaceReservation && (
        <TileMapPreview_timeslots workspaceReservation={workspaceReservation} />
      )}
      <TileItem
        data-testid="workspace-tile"
        aria-label={t('workplace:GroupBookingInvitationAriaLabel', {displayName: owner.name})}
        onKeyDown={handleKeyDown}
        onClick={() => {
          setModalPages([
            <BookingDetailsCard_timeslots
              date={date}
              workday={workday}
              workspaceReservation={workspaceReservation}
            />,
          ]);

          openModal();

          trackEvent('CalendarOverview_BookingDetails__Open', {
            date,
            isGroupBooking: true,
          });
        }}
        {...props}>
        <Avatar
          size={'small'}
          user={owner}
        />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h4>{t('workplace:GroupBooking')}</h4>
            <span>
              <p>
                {t('workplace:GroupReservationBookedBy', {
                  firstName: owner.name,
                  lastName: '',
                })}
              </p>
              {currentMember?.reservation.expiryDate && (
                <p className="text-mapiq-black-500">
                  {getGroupInvitationExpiryTime(t, currentMember.reservation.expiryDate)}
                </p>
              )}
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              as="div"
              onClick={(e) => {
                e.stopPropagation();
                declineInvitation();
              }}
              disabled={isSaving}
              button="secondary"
              size="small">
              {t('translation:Decline')}
            </Button>
            <Button
              as="div"
              onClick={(e) => {
                e.stopPropagation();
                acceptInvitation();
              }}
              disabled={isSaving}
              button="primary"
              size="small">
              {t('translation:Accept')}
            </Button>
          </div>
        </div>

        <Icon
          className="size-5 justify-self-end"
          icon={'caretRight'}
        />
      </TileItem>
    </>
  );
};
